<script>
    import { theme } from "$lib/utils/theme.svelte.ts";
</script>

<div class="starfield-bg" class:dark={theme.resolved === "dark"} aria-hidden="true">
    <!-- Starfield: 3 layers with different sizes and twinkle speeds — tiles vertically -->
    <div class="stars stars--small"></div>
    <div class="stars stars--medium"></div>
    <div class="stars stars--large"></div>

    <!-- Constellations SVG — centered in viewport, does NOT repeat -->
    <svg class="constellations" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid meet">
        <!-- Big Dipper — left -->
        <g class="constellation">
            <line x1="150" y1="280" x2="260" y2="255" />
            <line x1="260" y1="255" x2="280" y2="340" />
            <line x1="280" y1="340" x2="165" y2="370" />
            <line x1="165" y1="370" x2="150" y2="280" />
            <line x1="260" y1="255" x2="370" y2="225" />
            <line x1="370" y1="225" x2="485" y2="210" />
            <line x1="485" y1="210" x2="570" y2="240" />
            <circle cx="150" cy="280" r="3" class="star" style="--delay: 0s" />
            <circle cx="260" cy="255" r="3" class="star" style="--delay: 0.4s" />
            <circle cx="280" cy="340" r="2.5" class="star" style="--delay: 0.8s" />
            <circle cx="165" cy="370" r="2.5" class="star" style="--delay: 1.2s" />
            <circle cx="370" cy="225" r="3" class="star" style="--delay: 0.6s" />
            <circle cx="485" cy="210" r="3.5" class="star star--bright" style="--delay: 1.0s" />
            <circle cx="570" cy="240" r="3" class="star" style="--delay: 1.4s" />
        </g>

        <!-- Orion — center -->
        <g class="constellation">
            <line x1="870" y1="360" x2="910" y2="420" />
            <line x1="910" y1="420" x2="960" y2="410" />
            <line x1="960" y1="410" x2="1010" y2="400" />
            <line x1="1010" y1="400" x2="1050" y2="340" />
            <line x1="870" y1="360" x2="1050" y2="340" />
            <line x1="910" y1="420" x2="860" y2="530" />
            <line x1="1010" y1="400" x2="1070" y2="520" />
            <line x1="960" y1="410" x2="950" y2="480" />
            <circle cx="870" cy="360" r="4" class="star star--bright" style="--delay: 0.2s" />
            <circle cx="1050" cy="340" r="3.5" class="star star--bright" style="--delay: 0.7s" />
            <circle cx="910" cy="420" r="3" class="star" style="--delay: 0.3s" />
            <circle cx="960" cy="410" r="3" class="star" style="--delay: 0.9s" />
            <circle cx="1010" cy="400" r="3" class="star" style="--delay: 0.5s" />
            <circle cx="860" cy="530" r="3.5" class="star" style="--delay: 1.1s" />
            <circle cx="1070" cy="520" r="4" class="star star--bright" style="--delay: 0.1s" />
            <circle cx="950" cy="480" r="2" class="star" style="--delay: 1.3s" />
        </g>

        <!-- Aquarius — right -->
        <g class="constellation">
            <line x1="1400" y1="300" x2="1470" y2="360" />
            <line x1="1540" y1="310" x2="1470" y2="360" />
            <line x1="1470" y1="360" x2="1430" y2="420" />
            <line x1="1430" y1="420" x2="1500" y2="470" />
            <line x1="1500" y1="470" x2="1450" y2="530" />
            <line x1="1450" y1="530" x2="1530" y2="580" />
            <line x1="1530" y1="580" x2="1600" y2="560" />
            <line x1="1600" y1="560" x2="1670" y2="600" />
            <circle cx="1400" cy="300" r="3" class="star" style="--delay: 0.6s" />
            <circle cx="1540" cy="310" r="3" class="star" style="--delay: 1.0s" />
            <circle cx="1470" cy="360" r="3.5" class="star star--bright" style="--delay: 0.2s" />
            <circle cx="1430" cy="420" r="2.5" class="star" style="--delay: 0.8s" />
            <circle cx="1500" cy="470" r="2.5" class="star" style="--delay: 1.4s" />
            <circle cx="1450" cy="530" r="2.5" class="star" style="--delay: 0.4s" />
            <circle cx="1530" cy="580" r="3" class="star" style="--delay: 1.2s" />
            <circle cx="1600" cy="560" r="2.5" class="star" style="--delay: 0.9s" />
            <circle cx="1670" cy="600" r="2.5" class="star" style="--delay: 0.3s" />
        </g>
    </svg>
</div>

<style>
    .starfield-bg {
        position: fixed;
        inset: 0;
        z-index: -2;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.8s ease;
    }
    .starfield-bg.dark {
        opacity: 1;
    }

    /* ─── Star layers: tile vertically using repeating box-shadow ─── */
    /* Each layer's ::after covers a 400px tall tile.
       We repeat that tile pattern at +400px, +800px, +1200px, ... offsets
       so stars seamlessly fill the full viewport height. */

    .stars {
        position: absolute;
        inset: 0;
    }

    /* ─── Small stars (1px) ─── */
    .stars--small::after {
        content: "";
        position: absolute;
        width: 1px;
        height: 1px;
        background: transparent;
        /* tile height = 400px, repeated at 0, 400, 800, 1200, 1600, 2000, 2400, 2800, 3200, 3600 */
        box-shadow:
            /* row 0 (y: 0–399) */
            25px 15px #fff, 80px 42px #fff, 135px 8px #fff, 190px 67px #fff,
            245px 33px #fff, 310px 78px #fff, 365px 12px #fff, 420px 55px #fff,
            475px 28px #fff, 540px 71px #fff, 595px 5px #fff, 650px 48px #fff,
            705px 82px #fff, 45px 95px #fff, 155px 120px #fff, 270px 105px #fff,
            385px 130px #fff, 500px 98px #fff, 615px 115px #fff, 730px 140px #fff,
            60px 155px #fff, 175px 180px #fff, 290px 162px #fff, 405px 145px #fff,
            520px 175px #fff, 635px 190px #fff, 32px 200px #fff, 148px 215px #fff,
            263px 230px #fff, 378px 205px #fff, 493px 220px #fff, 608px 195px #fff,
            723px 210px #fff, 95px 250px #fff, 210px 265px #fff, 325px 240px #fff,
            440px 258px #fff, 555px 245px #fff, 670px 270px #fff, 110px 285px #fff,
            225px 300px #fff, 340px 290px #fff, 455px 310px #fff, 570px 280px #fff,
            685px 305px #fff, 50px 320px #fff, 165px 335px #fff, 280px 345px #fff,
            800px 30px #fff, 860px 95px #fff, 920px 55px #fff, 980px 120px #fff,
            1040px 18px #fff, 1100px 78px #fff, 1160px 42px #fff, 1220px 110px #fff,
            1280px 25px #fff, 1340px 88px #fff, 1400px 50px #fff, 1460px 105px #fff,
            1520px 35px #fff, 1580px 72px #fff, 1640px 15px #fff, 1700px 98px #fff,
            1760px 60px #fff, 1820px 130px #fff, 1880px 45px #fff, 1920px 85px #fff,
            815px 165px #fff, 875px 200px #fff, 935px 180px #fff, 995px 225px #fff,
            1055px 150px #fff, 1115px 195px #fff, 1175px 240px #fff, 1235px 170px #fff,
            1295px 210px #fff, 1355px 250px #fff, 1415px 185px #fff, 1475px 230px #fff,
            1535px 160px #fff, 1595px 220px #fff, 1655px 190px #fff, 1715px 260px #fff,
            830px 290px #fff, 890px 330px #fff, 950px 310px #fff, 1010px 360px #fff,
            1070px 285px #fff, 1130px 340px #fff, 1190px 300px #fff, 1250px 355px #fff,
            1310px 275px #fff, 1370px 320px #fff, 1430px 365px #fff, 1490px 295px #fff,
            /* row 1 (y: +400) */
            25px 415px #fff, 80px 442px #fff, 135px 408px #fff, 190px 467px #fff,
            245px 433px #fff, 310px 478px #fff, 365px 412px #fff, 420px 455px #fff,
            475px 428px #fff, 540px 471px #fff, 595px 405px #fff, 650px 448px #fff,
            705px 482px #fff, 45px 495px #fff, 155px 520px #fff, 270px 505px #fff,
            385px 530px #fff, 500px 498px #fff, 615px 515px #fff, 730px 540px #fff,
            60px 555px #fff, 175px 580px #fff, 290px 562px #fff, 405px 545px #fff,
            520px 575px #fff, 635px 590px #fff, 32px 600px #fff, 148px 615px #fff,
            263px 630px #fff, 378px 605px #fff, 493px 620px #fff, 608px 595px #fff,
            723px 610px #fff, 95px 650px #fff, 210px 665px #fff, 325px 640px #fff,
            440px 658px #fff, 555px 645px #fff, 670px 670px #fff, 110px 685px #fff,
            225px 700px #fff, 340px 690px #fff, 455px 710px #fff, 570px 680px #fff,
            685px 705px #fff, 50px 720px #fff, 165px 735px #fff, 280px 745px #fff,
            800px 430px #fff, 860px 495px #fff, 920px 455px #fff, 980px 520px #fff,
            1040px 418px #fff, 1100px 478px #fff, 1160px 442px #fff, 1220px 510px #fff,
            1280px 425px #fff, 1340px 488px #fff, 1400px 450px #fff, 1460px 505px #fff,
            1520px 435px #fff, 1580px 472px #fff, 1640px 415px #fff, 1700px 498px #fff,
            1760px 460px #fff, 1820px 530px #fff, 1880px 445px #fff, 1920px 485px #fff,
            815px 565px #fff, 875px 600px #fff, 935px 580px #fff, 995px 625px #fff,
            1055px 550px #fff, 1115px 595px #fff, 1175px 640px #fff, 1235px 570px #fff,
            1295px 610px #fff, 1355px 650px #fff, 1415px 585px #fff, 1475px 630px #fff,
            1535px 560px #fff, 1595px 620px #fff, 1655px 590px #fff, 1715px 660px #fff,
            830px 690px #fff, 890px 730px #fff, 950px 710px #fff, 1010px 760px #fff,
            1070px 685px #fff, 1130px 740px #fff, 1190px 700px #fff, 1250px 755px #fff,
            1310px 675px #fff, 1370px 720px #fff, 1430px 765px #fff, 1490px 695px #fff,
            /* row 2 (y: +800) */
            25px 815px #fff, 80px 842px #fff, 135px 808px #fff, 190px 867px #fff,
            245px 833px #fff, 310px 878px #fff, 365px 812px #fff, 420px 855px #fff,
            475px 828px #fff, 540px 871px #fff, 595px 805px #fff, 650px 848px #fff,
            705px 882px #fff, 45px 895px #fff, 155px 920px #fff, 270px 905px #fff,
            385px 930px #fff, 500px 898px #fff, 615px 915px #fff, 730px 940px #fff,
            60px 955px #fff, 175px 980px #fff, 290px 962px #fff, 405px 945px #fff,
            520px 975px #fff, 635px 990px #fff, 32px 1000px #fff, 148px 1015px #fff,
            263px 1030px #fff, 378px 1005px #fff, 493px 1020px #fff, 608px 995px #fff,
            723px 1010px #fff, 95px 1050px #fff, 210px 1065px #fff, 325px 1040px #fff,
            440px 1058px #fff, 555px 1045px #fff, 670px 1070px #fff, 110px 1085px #fff,
            225px 1100px #fff, 340px 1090px #fff, 455px 1110px #fff, 570px 1080px #fff,
            685px 1105px #fff, 50px 1120px #fff, 165px 1135px #fff, 280px 1145px #fff,
            800px 830px #fff, 860px 895px #fff, 920px 855px #fff, 980px 920px #fff,
            1040px 818px #fff, 1100px 878px #fff, 1160px 842px #fff, 1220px 910px #fff,
            1280px 825px #fff, 1340px 888px #fff, 1400px 850px #fff, 1460px 905px #fff,
            1520px 835px #fff, 1580px 872px #fff, 1640px 815px #fff, 1700px 898px #fff,
            1760px 860px #fff, 1820px 930px #fff, 1880px 845px #fff, 1920px 885px #fff,
            815px 965px #fff, 875px 1000px #fff, 935px 980px #fff, 995px 1025px #fff,
            1055px 950px #fff, 1115px 995px #fff, 1175px 1040px #fff, 1235px 970px #fff,
            1295px 1010px #fff, 1355px 1050px #fff, 1415px 985px #fff, 1475px 1030px #fff,
            1535px 960px #fff, 1595px 1020px #fff, 1655px 990px #fff, 1715px 1060px #fff,
            830px 1090px #fff, 890px 1130px #fff, 950px 1110px #fff, 1010px 1160px #fff,
            1070px 1085px #fff, 1130px 1140px #fff, 1190px 1100px #fff, 1250px 1155px #fff,
            1310px 1075px #fff, 1370px 1120px #fff, 1430px 1165px #fff, 1490px 1095px #fff,
            /* row 3 (y: +1200) */
            25px 1215px #fff, 80px 1242px #fff, 135px 1208px #fff, 190px 1267px #fff,
            245px 1233px #fff, 310px 1278px #fff, 365px 1212px #fff, 420px 1255px #fff,
            475px 1228px #fff, 540px 1271px #fff, 595px 1205px #fff, 650px 1248px #fff,
            800px 1230px #fff, 860px 1295px #fff, 920px 1255px #fff, 980px 1320px #fff,
            1040px 1218px #fff, 1100px 1278px #fff, 1160px 1242px #fff, 1220px 1310px #fff,
            1280px 1225px #fff, 1340px 1288px #fff, 1400px 1250px #fff, 1460px 1305px #fff,
            1520px 1235px #fff, 1580px 1272px #fff, 1640px 1215px #fff, 1700px 1298px #fff,
            1760px 1260px #fff, 1820px 1330px #fff, 1880px 1245px #fff, 1920px 1285px #fff,
            45px 1295px #fff, 155px 1320px #fff, 270px 1305px #fff, 385px 1330px #fff,
            500px 1298px #fff, 615px 1315px #fff, 730px 1340px #fff, 60px 1355px #fff,
            175px 1380px #fff, 290px 1362px #fff, 405px 1345px #fff, 520px 1375px #fff,
            635px 1390px #fff, 32px 1400px #fff, 148px 1415px #fff, 263px 1430px #fff,
            815px 1365px #fff, 875px 1400px #fff, 935px 1380px #fff, 995px 1425px #fff,
            1055px 1350px #fff, 1115px 1395px #fff, 1175px 1440px #fff, 1235px 1370px #fff,
            1295px 1410px #fff, 1355px 1450px #fff, 1415px 1385px #fff, 1475px 1430px #fff,
            1535px 1360px #fff, 1595px 1420px #fff, 1655px 1390px #fff, 1715px 1460px #fff,
            /* row 4 (y: +1600) */
            25px 1615px #fff, 80px 1642px #fff, 135px 1608px #fff, 190px 1667px #fff,
            245px 1633px #fff, 310px 1678px #fff, 365px 1612px #fff, 420px 1655px #fff,
            475px 1628px #fff, 540px 1671px #fff, 595px 1605px #fff, 650px 1648px #fff,
            800px 1630px #fff, 860px 1695px #fff, 920px 1655px #fff, 980px 1720px #fff,
            1040px 1618px #fff, 1100px 1678px #fff, 1160px 1642px #fff, 1220px 1710px #fff,
            1280px 1625px #fff, 1340px 1688px #fff, 1400px 1650px #fff, 1460px 1705px #fff,
            1520px 1635px #fff, 1580px 1672px #fff, 1640px 1615px #fff, 1700px 1698px #fff,
            1760px 1660px #fff, 1820px 1730px #fff, 1880px 1645px #fff, 1920px 1685px #fff,
            45px 1695px #fff, 155px 1720px #fff, 270px 1705px #fff, 385px 1730px #fff,
            500px 1698px #fff, 615px 1715px #fff, 730px 1740px #fff, 60px 1755px #fff,
            175px 1780px #fff, 290px 1762px #fff, 405px 1745px #fff, 520px 1775px #fff,
            635px 1790px #fff, 32px 1800px #fff, 148px 1815px #fff, 263px 1830px #fff,
            815px 1765px #fff, 875px 1800px #fff, 935px 1780px #fff, 995px 1825px #fff,
            1055px 1750px #fff, 1115px 1795px #fff, 1175px 1840px #fff, 1235px 1770px #fff,
            1295px 1810px #fff, 1355px 1850px #fff, 1415px 1785px #fff, 1475px 1830px #fff,
            1535px 1760px #fff, 1595px 1820px #fff, 1655px 1790px #fff, 1715px 1860px #fff,
            /* row 5 (y: +2000) */
            25px 2015px #fff, 80px 2042px #fff, 135px 2008px #fff, 190px 2067px #fff,
            245px 2033px #fff, 310px 2078px #fff, 365px 2012px #fff, 420px 2055px #fff,
            475px 2028px #fff, 540px 2071px #fff, 595px 2005px #fff, 650px 2048px #fff,
            800px 2030px #fff, 860px 2095px #fff, 920px 2055px #fff, 980px 2120px #fff,
            1040px 2018px #fff, 1100px 2078px #fff, 1160px 2042px #fff, 1220px 2110px #fff,
            1280px 2025px #fff, 1340px 2088px #fff, 1400px 2050px #fff, 1460px 2105px #fff,
            1520px 2035px #fff, 1580px 2072px #fff, 1640px 2015px #fff, 1700px 2098px #fff,
            1760px 2060px #fff, 1820px 2130px #fff, 1880px 2045px #fff, 1920px 2085px #fff,
            45px 2095px #fff, 155px 2120px #fff, 270px 2105px #fff, 385px 2130px #fff,
            500px 2098px #fff, 615px 2115px #fff, 730px 2140px #fff, 60px 2155px #fff,
            175px 2180px #fff, 290px 2162px #fff, 405px 2145px #fff, 520px 2175px #fff,
            635px 2190px #fff, 32px 2200px #fff, 148px 2215px #fff, 263px 2230px #fff,
            815px 2165px #fff, 875px 2200px #fff, 935px 2180px #fff, 995px 2225px #fff,
            1055px 2150px #fff, 1115px 2195px #fff, 1175px 2240px #fff, 1235px 2170px #fff,
            1295px 2210px #fff, 1355px 2250px #fff, 1415px 2185px #fff, 1475px 2230px #fff,
            1535px 2160px #fff, 1595px 2220px #fff, 1655px 2190px #fff, 1715px 2260px #fff,
            /* rows 6-9 (y: +2400 through +3600) — sparser to keep file manageable */
            25px 2415px #fff, 310px 2478px #fff, 595px 2405px #fff, 800px 2430px #fff,
            1040px 2418px #fff, 1280px 2425px #fff, 1520px 2435px #fff, 1760px 2460px #fff,
            155px 2520px #fff, 500px 2498px #fff, 730px 2540px #fff, 920px 2455px #fff,
            1160px 2442px #fff, 1400px 2450px #fff, 1640px 2415px #fff, 1880px 2445px #fff,
            60px 2555px #fff, 290px 2562px #fff, 520px 2575px #fff, 815px 2565px #fff,
            1055px 2550px #fff, 1295px 2510px #fff, 1535px 2560px #fff, 1700px 2598px #fff,
            175px 2680px #fff, 405px 2645px #fff, 635px 2690px #fff, 860px 2695px #fff,
            1100px 2678px #fff, 1340px 2688px #fff, 1580px 2672px #fff, 1820px 2730px #fff,
            25px 2815px #fff, 310px 2878px #fff, 595px 2805px #fff, 800px 2830px #fff,
            1040px 2818px #fff, 1280px 2825px #fff, 1520px 2835px #fff, 1760px 2860px #fff,
            155px 2920px #fff, 500px 2898px #fff, 730px 2940px #fff, 920px 2855px #fff,
            1160px 2842px #fff, 1400px 2850px #fff, 1640px 2815px #fff, 1880px 2845px #fff,
            60px 2955px #fff, 290px 2962px #fff, 520px 2975px #fff, 815px 2965px #fff,
            1055px 2950px #fff, 1295px 2910px #fff, 1535px 2960px #fff, 1700px 2998px #fff,
            25px 3215px #fff, 310px 3278px #fff, 595px 3205px #fff, 800px 3230px #fff,
            1040px 3218px #fff, 1280px 3225px #fff, 1520px 3235px #fff, 1760px 3260px #fff,
            155px 3320px #fff, 500px 3298px #fff, 730px 3340px #fff, 920px 3255px #fff,
            1160px 3242px #fff, 1400px 3250px #fff, 1640px 3215px #fff, 1880px 3245px #fff,
            60px 3355px #fff, 290px 3362px #fff, 520px 3375px #fff, 815px 3365px #fff,
            1055px 3350px #fff, 1295px 3310px #fff, 1535px 3360px #fff, 1700px 3398px #fff,
            175px 3480px #fff, 405px 3445px #fff, 635px 3490px #fff, 860px 3495px #fff,
            1100px 3478px #fff, 1340px 3488px #fff, 1580px 3472px #fff, 1820px 3530px #fff,
            25px 3615px #fff, 310px 3678px #fff, 595px 3605px #fff, 800px 3630px #fff,
            1040px 3618px #fff, 1280px 3625px #fff, 1520px 3635px #fff, 1760px 3660px #fff,
            155px 3720px #fff, 500px 3698px #fff, 730px 3740px #fff, 920px 3655px #fff,
            1160px 3642px #fff, 1400px 3650px #fff, 1640px 3615px #fff, 1880px 3645px #fff;
        animation: twinkle 4s ease-in-out infinite alternate;
    }

    /* ─── Medium stars (1.5px) ─── */
    .stars--medium::after {
        content: "";
        position: absolute;
        width: 1.5px;
        height: 1.5px;
        border-radius: 50%;
        background: transparent;
        box-shadow:
            /* row 0 */
            42px 28px #fff, 128px 62px #fff, 215px 18px #fff, 302px 85px #fff,
            388px 45px #fff, 475px 72px #fff, 562px 22px #fff, 648px 58px #fff,
            72px 108px #fff, 158px 142px #fff, 245px 112px #fff, 332px 165px #fff,
            418px 128px #fff, 505px 155px #fff, 592px 180px #fff, 678px 135px #fff,
            35px 195px #fff, 122px 228px #fff, 208px 205px #fff, 295px 250px #fff,
            382px 218px #fff, 468px 242px #fff, 555px 208px #fff, 642px 265px #fff,
            88px 278px #fff, 175px 312px #fff, 262px 295px #fff, 348px 328px #fff,
            780px 38px #fff, 850px 75px #fff, 940px 48px #fff, 1020px 92px #fff,
            1110px 32px #fff, 1190px 68px #fff, 1280px 55px #fff, 1360px 102px #fff,
            1450px 42px #fff, 1530px 82px #fff, 1620px 28px #fff, 1710px 95px #fff,
            1800px 52px #fff, 1870px 115px #fff, 785px 155px #fff, 870px 188px #fff,
            955px 168px #fff, 1035px 205px #fff, 1125px 148px #fff, 1205px 192px #fff,
            1295px 175px #fff, 1380px 218px #fff, 1465px 158px #fff, 1555px 198px #fff,
            1640px 172px #fff, 1730px 228px #fff, 795px 262px #fff, 885px 298px #fff,
            975px 278px #fff, 1060px 322px #fff, 1150px 258px #fff, 1240px 305px #fff,
            /* row 1 (y: +400) */
            42px 428px #fff, 128px 462px #fff, 215px 418px #fff, 302px 485px #fff,
            388px 445px #fff, 475px 472px #fff, 562px 422px #fff, 648px 458px #fff,
            72px 508px #fff, 158px 542px #fff, 245px 512px #fff, 332px 565px #fff,
            418px 528px #fff, 505px 555px #fff, 592px 580px #fff, 678px 535px #fff,
            35px 595px #fff, 122px 628px #fff, 208px 605px #fff, 295px 650px #fff,
            382px 618px #fff, 468px 642px #fff, 555px 608px #fff, 642px 665px #fff,
            88px 678px #fff, 175px 712px #fff, 262px 695px #fff, 348px 728px #fff,
            780px 438px #fff, 850px 475px #fff, 940px 448px #fff, 1020px 492px #fff,
            1110px 432px #fff, 1190px 468px #fff, 1280px 455px #fff, 1360px 502px #fff,
            1450px 442px #fff, 1530px 482px #fff, 1620px 428px #fff, 1710px 495px #fff,
            1800px 452px #fff, 1870px 515px #fff, 785px 555px #fff, 870px 588px #fff,
            955px 568px #fff, 1035px 605px #fff, 1125px 548px #fff, 1205px 592px #fff,
            1295px 575px #fff, 1380px 618px #fff, 1465px 558px #fff, 1555px 598px #fff,
            1640px 572px #fff, 1730px 628px #fff, 795px 662px #fff, 885px 698px #fff,
            975px 678px #fff, 1060px 722px #fff, 1150px 658px #fff, 1240px 705px #fff,
            /* row 2 (y: +800) */
            42px 828px #fff, 128px 862px #fff, 215px 818px #fff, 302px 885px #fff,
            388px 845px #fff, 475px 872px #fff, 562px 822px #fff, 648px 858px #fff,
            72px 908px #fff, 158px 942px #fff, 245px 912px #fff, 332px 965px #fff,
            418px 928px #fff, 505px 955px #fff, 592px 980px #fff, 678px 935px #fff,
            35px 995px #fff, 122px 1028px #fff, 208px 1005px #fff, 295px 1050px #fff,
            382px 1018px #fff, 468px 1042px #fff, 555px 1008px #fff, 642px 1065px #fff,
            88px 1078px #fff, 175px 1112px #fff, 262px 1095px #fff, 348px 1128px #fff,
            780px 838px #fff, 850px 875px #fff, 940px 848px #fff, 1020px 892px #fff,
            1110px 832px #fff, 1190px 868px #fff, 1280px 855px #fff, 1360px 902px #fff,
            1450px 842px #fff, 1530px 882px #fff, 1620px 828px #fff, 1710px 895px #fff,
            1800px 852px #fff, 1870px 915px #fff, 785px 955px #fff, 870px 988px #fff,
            955px 968px #fff, 1035px 1005px #fff, 1125px 948px #fff, 1205px 992px #fff,
            1295px 975px #fff, 1380px 1018px #fff, 1465px 958px #fff, 1555px 998px #fff,
            1640px 972px #fff, 1730px 1028px #fff, 795px 1062px #fff, 885px 1098px #fff,
            975px 1078px #fff, 1060px 1122px #fff, 1150px 1058px #fff, 1240px 1105px #fff,
            /* rows 3-9 — sparser continuation */
            42px 1228px #fff, 302px 1285px #fff, 562px 1222px #fff, 780px 1238px #fff,
            1020px 1292px #fff, 1280px 1255px #fff, 1530px 1282px #fff, 1800px 1252px #fff,
            158px 1342px #fff, 418px 1328px #fff, 678px 1335px #fff, 870px 1388px #fff,
            1110px 1332px #fff, 1360px 1302px #fff, 1620px 1328px #fff, 1870px 1315px #fff,
            88px 1478px #fff, 348px 1428px #fff, 555px 1408px #fff, 785px 1455px #fff,
            1035px 1405px #fff, 1295px 1475px #fff, 1465px 1458px #fff, 1730px 1428px #fff,
            42px 1628px #fff, 302px 1685px #fff, 562px 1622px #fff, 780px 1638px #fff,
            1020px 1692px #fff, 1280px 1655px #fff, 1530px 1682px #fff, 1800px 1652px #fff,
            158px 1742px #fff, 418px 1728px #fff, 678px 1735px #fff, 870px 1788px #fff,
            1110px 1732px #fff, 1360px 1702px #fff, 1620px 1728px #fff, 1870px 1715px #fff,
            88px 1878px #fff, 348px 1828px #fff, 555px 1808px #fff, 785px 1855px #fff,
            1035px 1805px #fff, 1295px 1875px #fff, 1465px 1858px #fff, 1730px 1828px #fff,
            42px 2028px #fff, 302px 2085px #fff, 562px 2022px #fff, 780px 2038px #fff,
            1020px 2092px #fff, 1280px 2055px #fff, 1530px 2082px #fff, 1800px 2052px #fff,
            158px 2142px #fff, 418px 2128px #fff, 678px 2135px #fff, 870px 2188px #fff,
            1110px 2132px #fff, 1360px 2102px #fff, 1620px 2128px #fff, 1870px 2115px #fff,
            42px 2428px #fff, 302px 2485px #fff, 562px 2422px #fff, 780px 2438px #fff,
            1020px 2492px #fff, 1280px 2455px #fff, 1530px 2482px #fff, 1800px 2452px #fff,
            158px 2542px #fff, 418px 2528px #fff, 678px 2535px #fff, 870px 2588px #fff,
            1110px 2532px #fff, 1360px 2502px #fff, 1620px 2528px #fff, 1870px 2515px #fff,
            42px 2828px #fff, 302px 2885px #fff, 562px 2822px #fff, 780px 2838px #fff,
            1020px 2892px #fff, 1280px 2855px #fff, 1530px 2882px #fff, 1800px 2852px #fff,
            158px 2942px #fff, 418px 2928px #fff, 678px 2935px #fff, 870px 2988px #fff,
            1110px 2932px #fff, 1360px 2902px #fff, 1620px 2928px #fff, 1870px 2915px #fff,
            42px 3228px #fff, 302px 3285px #fff, 562px 3222px #fff, 780px 3238px #fff,
            1020px 3292px #fff, 1280px 3255px #fff, 1530px 3282px #fff, 1800px 3252px #fff,
            158px 3342px #fff, 418px 3328px #fff, 678px 3335px #fff, 870px 3388px #fff,
            1110px 3332px #fff, 1360px 3302px #fff, 1620px 3328px #fff, 1870px 3315px #fff,
            42px 3628px #fff, 302px 3685px #fff, 562px 3622px #fff, 780px 3638px #fff,
            1020px 3692px #fff, 1280px 3655px #fff, 1530px 3682px #fff, 1800px 3652px #fff;
        animation: twinkle 5s ease-in-out infinite alternate;
        animation-delay: -1.5s;
    }

    /* ─── Large stars (2px) ─── */
    .stars--large::after {
        content: "";
        position: absolute;
        width: 2px;
        height: 2px;
        border-radius: 50%;
        background: transparent;
        box-shadow:
            /* row 0 */
            65px 35px #fff, 195px 52px #fff, 330px 25px #fff, 460px 68px #fff,
            590px 15px #fff, 105px 118px #fff, 235px 145px #fff, 370px 108px #fff,
            500px 158px #fff, 630px 125px #fff, 55px 215px #fff, 185px 248px #fff,
            315px 225px #fff, 445px 265px #fff, 575px 238px #fff, 140px 305px #fff,
            270px 335px #fff, 400px 298px #fff, 760px 42px #fff, 890px 65px #fff,
            1020px 28px #fff, 1150px 78px #fff, 1280px 48px #fff, 1410px 88px #fff,
            1540px 32px #fff, 1670px 72px #fff, 1800px 55px #fff, 810px 138px #fff,
            940px 168px #fff, 1070px 148px #fff, 1200px 185px #fff, 1330px 132px #fff,
            1460px 172px #fff, 1590px 152px #fff, 1720px 195px #fff, 850px 248px #fff,
            980px 275px #fff, 1110px 255px #fff, 1240px 298px #fff, 1370px 238px #fff,
            1500px 278px #fff, 1630px 258px #fff,
            /* row 1 (y: +400) */
            65px 435px #fff, 195px 452px #fff, 330px 425px #fff, 460px 468px #fff,
            590px 415px #fff, 105px 518px #fff, 235px 545px #fff, 370px 508px #fff,
            500px 558px #fff, 630px 525px #fff, 55px 615px #fff, 185px 648px #fff,
            315px 625px #fff, 445px 665px #fff, 575px 638px #fff, 140px 705px #fff,
            270px 735px #fff, 400px 698px #fff, 760px 442px #fff, 890px 465px #fff,
            1020px 428px #fff, 1150px 478px #fff, 1280px 448px #fff, 1410px 488px #fff,
            1540px 432px #fff, 1670px 472px #fff, 1800px 455px #fff, 810px 538px #fff,
            940px 568px #fff, 1070px 548px #fff, 1200px 585px #fff, 1330px 532px #fff,
            1460px 572px #fff, 1590px 552px #fff, 1720px 595px #fff, 850px 648px #fff,
            980px 675px #fff, 1110px 655px #fff, 1240px 698px #fff, 1370px 638px #fff,
            1500px 678px #fff, 1630px 658px #fff,
            /* row 2 (y: +800) */
            65px 835px #fff, 195px 852px #fff, 330px 825px #fff, 460px 868px #fff,
            590px 815px #fff, 105px 918px #fff, 235px 945px #fff, 370px 908px #fff,
            500px 958px #fff, 630px 925px #fff, 55px 1015px #fff, 185px 1048px #fff,
            315px 1025px #fff, 445px 1065px #fff, 575px 1038px #fff, 140px 1105px #fff,
            270px 1135px #fff, 400px 1098px #fff, 760px 842px #fff, 890px 865px #fff,
            1020px 828px #fff, 1150px 878px #fff, 1280px 848px #fff, 1410px 888px #fff,
            1540px 832px #fff, 1670px 872px #fff, 1800px 855px #fff, 810px 938px #fff,
            940px 968px #fff, 1070px 948px #fff, 1200px 985px #fff, 1330px 932px #fff,
            1460px 972px #fff, 1590px 952px #fff, 1720px 995px #fff, 850px 1048px #fff,
            980px 1075px #fff, 1110px 1055px #fff, 1240px 1098px #fff, 1370px 1038px #fff,
            1500px 1078px #fff, 1630px 1058px #fff,
            /* rows 3-9 — sparser */
            65px 1235px #fff, 330px 1225px #fff, 590px 1215px #fff, 760px 1242px #fff,
            1020px 1228px #fff, 1280px 1248px #fff, 1540px 1232px #fff, 1800px 1255px #fff,
            235px 1345px #fff, 500px 1358px #fff, 810px 1338px #fff, 1070px 1348px #fff,
            1330px 1332px #fff, 1590px 1352px #fff, 140px 1405px #fff, 400px 1398px #fff,
            65px 1635px #fff, 330px 1625px #fff, 590px 1615px #fff, 760px 1642px #fff,
            1020px 1628px #fff, 1280px 1648px #fff, 1540px 1632px #fff, 1800px 1655px #fff,
            235px 1745px #fff, 500px 1758px #fff, 810px 1738px #fff, 1070px 1748px #fff,
            1330px 1732px #fff, 1590px 1752px #fff, 140px 1805px #fff, 400px 1798px #fff,
            65px 2035px #fff, 330px 2025px #fff, 590px 2015px #fff, 760px 2042px #fff,
            1020px 2028px #fff, 1280px 2048px #fff, 1540px 2032px #fff, 1800px 2055px #fff,
            235px 2145px #fff, 500px 2158px #fff, 810px 2138px #fff, 1070px 2148px #fff,
            1330px 2132px #fff, 1590px 2152px #fff, 140px 2205px #fff, 400px 2198px #fff,
            65px 2435px #fff, 330px 2425px #fff, 590px 2415px #fff, 760px 2442px #fff,
            1020px 2428px #fff, 1280px 2448px #fff, 1540px 2432px #fff, 1800px 2455px #fff,
            235px 2545px #fff, 500px 2558px #fff, 810px 2538px #fff, 1070px 2548px #fff,
            65px 2835px #fff, 330px 2825px #fff, 590px 2815px #fff, 760px 2842px #fff,
            1020px 2828px #fff, 1280px 2848px #fff, 1540px 2832px #fff, 1800px 2855px #fff,
            235px 2945px #fff, 500px 2958px #fff, 810px 2938px #fff, 1070px 2948px #fff,
            65px 3235px #fff, 330px 3225px #fff, 590px 3215px #fff, 760px 3242px #fff,
            1020px 3228px #fff, 1280px 3248px #fff, 1540px 3232px #fff, 1800px 3255px #fff,
            235px 3345px #fff, 500px 3358px #fff, 810px 3338px #fff, 1070px 3348px #fff,
            65px 3635px #fff, 330px 3625px #fff, 590px 3615px #fff, 760px 3642px #fff,
            1020px 3628px #fff, 1280px 3648px #fff, 1540px 3632px #fff, 1800px 3655px #fff;
        animation: twinkle 3.5s ease-in-out infinite alternate;
        animation-delay: -2.5s;
    }

    @keyframes twinkle {
        from { opacity: 0.3; }
        to { opacity: 1; }
    }

    /* ─── Constellations — centered in viewport, no repeat ─── */
    .constellations {
        position: fixed;
        top: 50%;
        left: 50%;
        translate: -50% -50%;
        width: 100vw;
        height: 100vh;
        pointer-events: none;
    }

    .constellation line {
        stroke: rgba(255, 255, 255, 0.18);
        stroke-width: 1;
    }

    .constellation .star {
        fill: #fff;
        animation: twinkle-star 3s ease-in-out infinite alternate;
        animation-delay: var(--delay, 0s);
    }
    .constellation .star--bright {
        fill: #fff;
        filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.8));
        animation-duration: 2.5s;
    }

    @keyframes twinkle-star {
        from { opacity: 0.4; }
        to { opacity: 1; }
    }

    /* ─── Reduced motion ─── */
    @media (prefers-reduced-motion: reduce) {
        .stars--small::after,
        .stars--medium::after,
        .stars--large::after,
        .constellation .star,
        .constellation .star--bright {
            animation: none;
        }
    }
</style>
